// src/pages/admin/TeacherWorkload.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { ref, get } from 'firebase/database';

const TeacherWorkload = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workloadData, setWorkloadData] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherDetails, setTeacherDetails] = useState(null);
  const [allTimetables, setAllTimetables] = useState({});
  const [teachers, setTeachers] = useState([]);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const periods = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'];

  // Check admin authentication
  useEffect(() => {
    const isAdminLoggedIn = sessionStorage.getItem('isAdminLoggedIn');
    if (!isAdminLoggedIn) {
      navigate('/login');
    }
  }, [navigate]);

  // Load data and calculate workload
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [teachersSnap, timetablesSnap] = await Promise.all([
          get(ref(db, 'teachers')),
          get(ref(db, 'timetables'))
        ]);

        let teachersList = [];
        if (teachersSnap.exists()) {
          const teachersData = teachersSnap.val();
          teachersList = Object.values(teachersData).filter(t => t.status !== 'inactive');
          setTeachers(teachersList);
        }

        let timetablesData = {};
        if (timetablesSnap.exists()) {
          timetablesData = timetablesSnap.val();
          setAllTimetables(timetablesData);
        }

        // Calculate workload
        if (teachersList.length > 0 && Object.keys(timetablesData).length > 0) {
          calculateWorkload(teachersList, timetablesData);
        } else {
          setWorkloadData([]);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateWorkload = (teachersList, timetablesData) => {
    const workloadMap = {};

    // Initialize workload for all teachers
    teachersList.forEach(teacher => {
      workloadMap[teacher.name] = {
        name: teacher.name,
        totalPeriods: 0,
        subjects: new Set(),
        classes: new Set(),
        dailyBreakdown: {
          Monday: 0, Tuesday: 0, Wednesday: 0,
          Thursday: 0, Friday: 0, Saturday: 0
        },
        subjectCount: {}
      };
    });

    // Scan all timetables
    Object.entries(timetablesData).forEach(([className, classData]) => {
      if (!classData || typeof classData !== 'object') return;

      Object.entries(classData).forEach(([day, dayData]) => {
        if (!dayData || typeof dayData !== 'object') return;

        Object.entries(dayData).forEach(([period, periodData]) => {
          if (!periodData || typeof periodData !== 'object') return;

          const teacherName = periodData.teacher;
          const subject = periodData.subject || 'Unknown';

          if (workloadMap[teacherName]) {
            workloadMap[teacherName].totalPeriods++;
            workloadMap[teacherName].subjects.add(subject);
            workloadMap[teacherName].classes.add(className);
            
            if (workloadMap[teacherName].dailyBreakdown[day] !== undefined) {
              workloadMap[teacherName].dailyBreakdown[day]++;
            }

            if (!workloadMap[teacherName].subjectCount[subject]) {
              workloadMap[teacherName].subjectCount[subject] = 0;
            }
            workloadMap[teacherName].subjectCount[subject]++;
          }
        });
      });
    });

    // Convert to array and sort by total periods
    const workloadArray = Object.values(workloadMap)
      .map(item => ({
        ...item,
        subjects: Array.from(item.subjects),
        classes: Array.from(item.classes)
      }))
      .sort((a, b) => b.totalPeriods - a.totalPeriods);

    setWorkloadData(workloadArray);
    setLoading(false);
  };

  // Handle teacher click for details
  const handleTeacherClick = (teacher) => {
    if (selectedTeacher?.name === teacher.name) {
      setSelectedTeacher(null);
      setTeacherDetails(null);
    } else {
      setSelectedTeacher(teacher);
      setTeacherDetails(teacher);
    }
  };

  // Handle logout
  const handleLogout = () => {
    sessionStorage.removeItem('isAdminLoggedIn');
    navigate('/login');
  };

  // Go back to dashboard
  const handleBack = () => {
    navigate('/admin');
  };

  // Calculate summary stats
  const getSummaryStats = () => {
    const totalTeachers = workloadData.length;
    const totalPeriods = workloadData.reduce((sum, t) => sum + t.totalPeriods, 0);
    const avgPeriods = totalTeachers > 0 ? Math.round(totalPeriods / totalTeachers) : 0;
    const maxPeriods = workloadData.length > 0 ? Math.max(...workloadData.map(t => t.totalPeriods)) : 0;
    const minPeriods = workloadData.length > 0 ? Math.min(...workloadData.map(t => t.totalPeriods)) : 0;
    
    return { totalTeachers, totalPeriods, avgPeriods, maxPeriods, minPeriods };
  };

  const stats = getSummaryStats();

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={handleBack} style={styles.backBtn}>← Back</button>
          <h1 style={styles.headerTitle}>📈 Teacher Workload</h1>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          🚪 Logout
        </button>
      </header>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Calculating workload...</p>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <span style={styles.statNumber}>{stats.totalTeachers}</span>
                <span style={styles.statLabel}>Total Teachers</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statNumber}>{stats.totalPeriods}</span>
                <span style={styles.statLabel}>Total Weekly Periods</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statNumber}>{stats.avgPeriods}</span>
                <span style={styles.statLabel}>Avg Periods/Teacher</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statNumber}>{stats.maxPeriods}</span>
                <span style={styles.statLabel}>Max Periods</span>
              </div>
            </div>

            {/* Workload List */}
            <div style={styles.workloadSection}>
              <h2 style={styles.sectionTitle}>Teacher Workload Distribution</h2>
              
              {workloadData.length === 0 ? (
                <div style={styles.noDataMessage}>
                  <div style={styles.noDataIcon}>📋</div>
                  <h3>No Workload Data</h3>
                  <p>No timetable data available to calculate workload.</p>
                </div>
              ) : (
                <div style={styles.workloadList}>
                  {workloadData.map((teacher, index) => (
                    <div
                      key={index}
                      style={styles.workloadItem}
                      onClick={() => handleTeacherClick(teacher)}
                    >
                      <div style={styles.workloadInfo}>
                        <div style={styles.workloadName}>{teacher.name}</div>
                        <div style={styles.workloadSubjects}>{teacher.subjects.join(', ')}</div>
                      </div>
                      <div style={styles.workloadStats}>
                        <span style={styles.workloadCount}>{teacher.totalPeriods}</span>
                        <span style={styles.workloadLabel}>Classes</span>
                        <span style={styles.workloadArrow}>▶</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Teacher Details Modal */}
            {teacherDetails && (
              <div style={styles.modalOverlay} onClick={() => {
                setSelectedTeacher(null);
                setTeacherDetails(null);
              }}>
                <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                  <button 
                    style={styles.modalClose} 
                    onClick={() => {
                      setSelectedTeacher(null);
                      setTeacherDetails(null);
                    }}
                  >
                    ×
                  </button>
                  
                  <div style={styles.modalHeader}>
                    <h2>👨‍🏫 {teacherDetails.name}</h2>
                  </div>

                  <div style={styles.modalBody}>
                    {/* Total Weekly Classes */}
                    <div style={styles.detailSection}>
                      <div style={styles.totalClassesCard}>
                        <span style={styles.totalClassesNumber}>{teacherDetails.totalPeriods}</span>
                        <span style={styles.totalClassesLabel}>Total Weekly Classes</span>
                      </div>
                    </div>

                    {/* Daily Breakdown */}
                    <div style={styles.detailSection}>
                      <h3 style={styles.detailSectionTitle}>📅 Daily Breakdown</h3>
                      <div style={styles.dailyGrid}>
                        {Object.entries(teacherDetails.dailyBreakdown).map(([day, count]) => (
                          <div key={day} style={styles.dailyItem}>
                            <span style={styles.dailyDay}>{day}</span>
                            <span style={styles.dailyCount}>{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Subject Workload */}
                    <div style={styles.detailSection}>
                      <h3 style={styles.detailSectionTitle}>📖 Subject Workload</h3>
                      <div style={styles.subjectList}>
                        {Object.entries(teacherDetails.subjectCount).map(([subject, count]) => (
                          <div key={subject} style={styles.subjectItem}>
                            <span>{subject}</span>
                            <span>{count} periods</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Classes */}
                    <div style={styles.detailSection}>
                      <h3 style={styles.detailSectionTitle}>🏫 Classes ({teacherDetails.classes.length})</h3>
                      <div style={styles.classTags}>
                        {teacherDetails.classes.map((cls, idx) => (
                          <span key={idx} style={styles.classTag}>{cls}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>Powered by Jauhari WebTech</p>
        <p>© 2026 Jauhari WebTech. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f7fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
  },

  // Header
  header: {
    background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 100%)',
    color: 'white',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '6px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  headerTitle: {
    fontSize: '18px',
    margin: 0,
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '8px 18px',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },

  // Main Content
  mainContent: {
    flex: 1,
    padding: '15px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },

  // Loading
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTop: '4px solid #2d1b4e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  // Stats Grid
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  },
  statCard: {
    background: 'white',
    padding: '15px',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  statNumber: {
    display: 'block',
    fontSize: '28px',
    fontWeight: 700,
    color: '#2d1b4e',
  },
  statLabel: {
    fontSize: '11px',
    color: '#7f8c8d',
    marginTop: '4px',
  },

  // Workload Section
  workloadSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  sectionTitle: {
    fontSize: '18px',
    color: '#2c3e50',
    margin: '0 0 15px 0',
  },
  workloadList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  workloadItem: {
    background: '#f8f9fa',
    padding: '14px 16px',
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderLeft: '4px solid #2d1b4e',
  },
  workloadInfo: {
    flex: 1,
  },
  workloadName: {
    fontWeight: 600,
    color: '#2c3e50',
    fontSize: '15px',
  },
  workloadSubjects: {
    fontSize: '12px',
    color: '#7f8c8d',
    marginTop: '2px',
  },
  workloadStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginLeft: '10px',
  },
  workloadCount: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#2d1b4e',
  },
  workloadLabel: {
    fontSize: '10px',
    color: '#7f8c8d',
  },
  workloadArrow: {
    fontSize: '14px',
    color: '#bdc3c7',
  },

  // No Data
  noDataMessage: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  noDataIcon: {
    fontSize: '48px',
    marginBottom: '10px',
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 2000,
    animation: 'fadeIn 0.3s ease',
  },
  modalContent: {
    background: 'white',
    borderRadius: '16px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '80vh',
    overflowY: 'auto',
    padding: '25px',
    position: 'relative',
    animation: 'slideUp 0.3s ease',
  },
  modalClose: {
    position: 'absolute',
    top: '12px',
    right: '16px',
    background: 'none',
    border: 'none',
    fontSize: '28px',
    color: '#7f8c8d',
    cursor: 'pointer',
    padding: '0',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalHeader: {
    marginBottom: '20px',
    borderBottom: '2px solid #f0f0f0',
    paddingBottom: '15px',
  },
  'modalHeader h2': {
    margin: 0,
    color: '#2c3e50',
    fontSize: '22px',
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  detailSection: {
    marginBottom: '5px',
  },
  detailSectionTitle: {
    color: '#2c3e50',
    fontSize: '15px',
    marginBottom: '8px',
  },
  totalClassesCard: {
    background: '#f0f4ff',
    padding: '15px',
    borderRadius: '10px',
    textAlign: 'center',
    border: '1px solid #d4dfff',
  },
  totalClassesNumber: {
    display: 'block',
    fontSize: '32px',
    fontWeight: 700,
    color: '#2d1b4e',
  },
  totalClassesLabel: {
    fontSize: '13px',
    color: '#7f8c8d',
  },
  dailyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  },
  dailyItem: {
    background: '#f8f9fa',
    padding: '8px 12px',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
  },
  dailyDay: {
    color: '#2c3e50',
  },
  dailyCount: {
    fontWeight: 600,
    color: '#2d1b4e',
  },
  subjectList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  subjectItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 12px',
    background: '#f8f9fa',
    borderRadius: '6px',
    fontSize: '13px',
  },
  subjectItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 12px',
    background: '#f8f9fa',
    borderRadius: '6px',
    fontSize: '13px',
  },
  subjectItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 12px',
    background: '#f8f9fa',
    borderRadius: '6px',
    fontSize: '13px',
  },
  classTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  classTag: {
    background: '#2d1b4e',
    color: 'white',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 500,
  },

  // Footer
  footer: {
    background: 'white',
    textAlign: 'center',
    padding: '12px',
    borderTop: '1px solid #e0e0e0',
    marginTop: '20px',
  },
};

// Add keyframe animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;
document.head.appendChild(styleSheet);

export default TeacherWorkload;